import React from "react";
import { useNavigate } from "react-router";
import { zeigeGlobalenHinweis } from "../calendar/globalerHinweis";
import { openMailVorgangPicker } from "../mail/MailVorgangPickerWindow";
import { openMailContactAssign } from "../mail/MailContactAssignWindow";
import { vorgangStartAus, type MailZuweisungErgebnis } from "../mail/mailContactAssign";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openVorgangCreateFlow, type VorgangCreateResult } from "../vorgang/create/VorgangCreateFlowWindow";
import type { VorgangFuerVorgabe } from "../vorgang/create/vorgangFuer";
import { openDvagPostAblage } from "./DvagPostAblageFenster";
import {
    dvagArtZeile,
    dvagDatum,
    dvagPostService,
    meldeDvagPostGeaendert,
    type DvagPostDetail,
    type DvagPostZeile,
} from "../../services/dvagPostService";

/** T-298: Wohin ein Eintrag beim Kunden gewandert ist — für den Hinweis danach. */
export type DvagAbgelegtHinweis = { contactId: number; kontaktName: string; ordner: string };

export interface DvagPostAktionenOptionen {
    /** Neuer Stand eines Eintrags; Liste und Detail ziehen nach. */
    onGeaendert: (neu: DvagPostDetail) => void;
    /** T-298: Der Eintrag ist beim Kunden abgelegt und damit verschwunden. */
    onEntfernt: (id: number, hinweis: DvagAbgelegtHinweis) => void;
    /** Fehler einer Aktion; `null` räumt eine frühere Meldung weg. */
    onFehler: (text: string | null) => void;
}

const dokumentName = (e: DvagPostZeile): string => e.pdfName || `${e.betreff || "Dokument"}.pdf`;

/**
 * Die Aktionen eines Eintrags der Kontakthistorie — für den Kopf des geöffneten
 * Eintrags UND das Kontextmenü der Liste (T-293, 28.10.2026). Bis dahin lagen sie
 * nur im Detail; ein zweites Menü sollte keine Zweitfassung bekommen.
 *
 * Jede Aktion nimmt den Eintrag als Argument: die Liste nutzt EINEN Satz
 * Aktionen für alle Zeilen.
 */
export function useDvagPostAktionen(optionen: DvagPostAktionenOptionen) {
    const navigate = useNavigate();
    const { openWindow } = useWindowManager();
    const [beschaeftigt, setBeschaeftigt] = React.useState(false);
    // Die Rückrufe wechseln mit jedem Render der Liste die Identität; Fenster
    // (Vorgangsauswahl, Kontakt zuweisen) rufen sie erst viel später.
    const ref = React.useRef(optionen);
    ref.current = optionen;

    const fuehreAus = async (aktion: () => Promise<DvagPostDetail>, fehlertext: string): Promise<void> => {
        setBeschaeftigt(true);
        ref.current.onFehler(null);
        try {
            const neu = await aktion();
            ref.current.onGeaendert(neu);
            meldeDvagPostGeaendert();
        } catch {
            ref.current.onFehler(fehlertext);
        } finally {
            setBeschaeftigt(false);
        }
    };

    /**
     * Eintrag an Vorgang oder To-do binden: Der Server legt das PDF dort als Dokument
     * ab und archiviert den Eintrag. Wirft bei einem Fehler, damit der Aufrufer ihn zeigt.
     * T-397: `dateiname` ist der im Anlage-Fenster geänderte Name des Dokuments.
     */
    const verknuepfe = async (
        eintrag: DvagPostZeile,
        ziel: { vorgangId?: number; todoId?: number },
        dateiname?: string,
    ): Promise<void> => {
        const neu = await dvagPostService.verknuepfeVorgang(eintrag.id, dateiname ? { ...ziel, dateiname } : ziel);
        if (eintrag.hatPdf && neu.dokumentAngehaengt === false) {
            zeigeGlobalenHinweis(
                "Der Eintrag ist dem Vorgang zugeordnet und archiviert. Das Dokument konnte aber nicht angehängt werden; es liegt weiter in der Kontakthistorie (Archiv).",
                { art: "fehler" },
            );
        } else {
            zeigeGlobalenHinweis(
                eintrag.hatPdf
                    ? "Dem Vorgang zugeordnet, Dokument angehängt. Der Eintrag liegt jetzt im Archiv."
                    : "Dem Vorgang zugeordnet. Der Eintrag liegt jetzt im Archiv.",
                { art: "info" },
            );
        }
        ref.current.onGeaendert(neu);
        meldeDvagPostGeaendert();
    };

    const neuerVorgang = (
        eintrag: DvagPostZeile,
        start?: { kontakt: { id: number; displayName: string } | null; vorgangFuer?: VorgangFuerVorgabe },
    ): void => {
        // Kommt die Wahl aus „Kontakt zuweisen", gilt sie – auch ein ausdrückliches „ohne Kontakt".
        const kontakt = start
            ? start.kontakt && { id: start.kontakt.id, name: start.kontakt.displayName }
            : (eintrag.kontakt ?? eintrag.vorschlag);
        const notiz = [
            `DVAG-Kontakthistorie vom ${dvagDatum(eintrag.datum)}`,
            dvagArtZeile(eintrag),
            eintrag.produktgeber ? `Produktgeber: ${eintrag.produktgeber}` : null,
            eintrag.nummer ? `Nummer: ${eintrag.nummer}` : null,
        ]
            .filter(Boolean)
            .join("\n");
        openVorgangCreateFlow(openWindow, {
            initialContactId: kontakt?.id ?? null,
            initialContactName: kontakt?.name ?? null,
            initialSubject: eintrag.betreff,
            initialNote: notiz,
            vorgangFuer: start?.vorgangFuer,
            // T-397: Das PDF lässt sich im Fenster umbenennen, bevor es am Vorgang landet.
            eintragDokument: eintrag.hatPdf ? { name: dokumentName(eintrag) } : undefined,
            onCreated: async (ergebnis: VorgangCreateResult) => {
                // Im Flow lässt sich auch „einem bestehenden Vorgang zuweisen" wählen: dann
                // entsteht ein To-do, und das Dokument gehört an dieses To-do.
                const ziel = ergebnis.vorgangId
                    ? { vorgangId: ergebnis.vorgangId }
                    : ergebnis.todoId
                      ? { todoId: ergebnis.todoId }
                      : null;
                if (!ziel) return;
                try {
                    await verknuepfe(eintrag, ziel, ergebnis.dokumentName);
                } catch {
                    zeigeGlobalenHinweis(
                        "Der Vorgang ist angelegt, das Dokument der Kontakthistorie konnte aber nicht angehängt werden. Der Eintrag bleibt im Posteingang.",
                        { art: "fehler" },
                    );
                }
            },
        });
    };

    /** Dieselbe Vorgangs-Auswahl wie bei Mails („Einem Vorgang zuweisen"). */
    const bestehendemVorgangZuordnen = (eintrag: DvagPostZeile): void => {
        const kontakt = eintrag.kontakt ?? eintrag.vorschlag;
        openMailVorgangPicker(openWindow, {
            contactId: kontakt?.id ?? null,
            mailBetreff: eintrag.betreff || null,
            // T-397: Das PDF lässt sich in der Auswahl umbenennen, bevor es am Vorgang landet.
            dokument: eintrag.hatPdf ? { name: dokumentName(eintrag) } : undefined,
            // Ein Eintrag gehört zu genau einem Vorgang: bei mehreren Haken gilt der erste.
            onAssign: async (ziele, _navigationVorgangId, neuerName) => {
                const ziel = ziele[0];
                if (ziel) await verknuepfe(eintrag, ziel, neuerName);
            },
        });
    };

    /** Dasselbe Popup wie bei Mails: Kontakt suchen oder anlegen, danach bei Bedarf ein Vorgang. */
    const kontaktZuweisen = (eintrag: DvagPostZeile): void => {
        const bisher = eintrag.kontakt ?? eintrag.vorschlag;
        openMailContactAssign(openWindow, {
            senderEmail: null,
            senderName: eintrag.name || null,
            aktuelleZuweisung: eintrag.kontakt ? { id: eintrag.kontakt.id, displayName: eintrag.kontakt.name } : null,
            gegenstand: {
                name: "Der Eintrag",
                kopf: (
                    <>
                        Eintrag der Kontakthistorie{eintrag.name ? <> für <strong>{eintrag.name}</strong></> : null}
                        {eintrag.nummer ? ` (Nr. ${eintrag.nummer})` : ""}.{" "}
                        {bisher ? (
                            <>
                                {eintrag.kontakt ? "Zugewiesen" : "Vorschlag"}: <strong>{bisher.name}</strong>.
                            </>
                        ) : (
                            "Es wurde kein Kontakt erkannt."
                        )}
                    </>
                ),
            },
            onResolved: async (ergebnis: MailZuweisungErgebnis) => {
                if (ergebnis.kontakt) {
                    const kontaktId = ergebnis.kontakt.id;
                    await fuehreAus(
                        () => dvagPostService.ordneZu(eintrag.id, kontaktId),
                        "Die Zuordnung konnte nicht gespeichert werden.",
                    );
                }
                if (ergebnis.weiter === "vorgang") neuerVorgang(eintrag, vorgangStartAus(ergebnis));
            },
        });
    };

    /** Den erkannten Vorschlag als Kontakt übernehmen. */
    const vorschlagUebernehmen = (eintrag: DvagPostZeile): Promise<void> => {
        const vorschlag = eintrag.vorschlag;
        if (!vorschlag) return Promise.resolve();
        return fuehreAus(
            () => dvagPostService.ordneZu(eintrag.id, vorschlag.id),
            "Die Zuordnung konnte nicht gespeichert werden.",
        );
    };

    /** T-298: Kontakt + Ordner der Kundenakte wählen; danach ist der Eintrag weg. */
    const beimKundenAblegen = (eintrag: DvagPostZeile): void => {
        const id = eintrag.id;
        openDvagPostAblage(openWindow, {
            eintragId: id,
            dateiname: dokumentName(eintrag),
            kontakt: eintrag.kontakt ?? (eintrag.vorschlag ? { id: eintrag.vorschlag.id, name: eintrag.vorschlag.name } : null),
            onAbgelegt: (ergebnis) => {
                ref.current.onEntfernt(id, ergebnis);
                meldeDvagPostGeaendert();
            },
        });
    };

    /** Archivieren bzw. aus dem Archiv zurück in den Posteingang. */
    const archivUmschalten = (eintrag: DvagPostZeile): Promise<void> => {
        const archiviert = eintrag.archiviertAm != null;
        return fuehreAus(
            () => dvagPostService.setzeArchiviert(eintrag.id, !archiviert),
            archiviert ? "Der Eintrag konnte nicht zurückgeholt werden." : "Der Eintrag konnte nicht archiviert werden.",
        );
    };

    const gelesenUmschalten = (eintrag: DvagPostZeile): Promise<void> =>
        fuehreAus(
            () => dvagPostService.setzeGelesen(eintrag.id, !eintrag.gelesen),
            "Der Lesestatus konnte nicht gespeichert werden.",
        );

    const vorgangOeffnen = (eintrag: DvagPostZeile): void => {
        if (eintrag.vorgangId) navigate(`/vorgaenge/v4/${eintrag.vorgangId}`);
    };

    return {
        beschaeftigt,
        neuerVorgang,
        bestehendemVorgangZuordnen,
        kontaktZuweisen,
        vorschlagUebernehmen,
        beimKundenAblegen,
        archivUmschalten,
        gelesenUmschalten,
        vorgangOeffnen,
    };
}
